/**
 * Sample Purchase Button Handler (Production)
 * Author: Julian van Wijk
 *
 * Update (Jan 2026):
 * - Removed visible "Loading..." label flicker.
 * - Uses disabled state only (no text changes) for background resolve.
 * - On click: keeps original label (no loading text), disables button to prevent double click.
 * - Optional: adds data-state attributes for CSS styling if desired.
 */

(() => {
  const API_ENDPOINT = 'https://k4ktvim6coum72duphptcgrg3q0djixv.lambda-url.eu-west-2.on.aws/';
  const API_KEY = 'evita_2026_01_06_7b2f9c1d6a9e4b11a3e2f7c9';

  // Network tuning
  const API_TIMEOUT_MS = 2500;
  const STORAGE_PREFIX = 'sample_variant_id:';

  function getHeaderSafeText(el) {
    return (el?.textContent || '').trim();
  }

  function normalizeBaseSku(raw) {
    const sku = String(raw || '').trim();
    return sku.length ? sku : null;
  }

  function toSampleSku(baseSku) {
    return `SFS${baseSku}`;
  }

  function storageKey(sampleSku) {
    return `${STORAGE_PREFIX}${sampleSku}`;
  }

  function readCachedVariantId(sampleSku) {
    const v = sessionStorage.getItem(storageKey(sampleSku));
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  function writeCachedVariantId(sampleSku, variantId) {
    sessionStorage.setItem(storageKey(sampleSku), String(variantId));
  }

  function fetchWithTimeout(url, options = {}, timeoutMs = API_TIMEOUT_MS) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(id));
  }

  /**
   * Minimal button state control:
   * - No label changes for loading.
   * - "unavailable" shows a label change (requested earlier).
   * - "ready" restores original label.
   */
  function setButtonState(btn, state, originalLabel) {
    const prev = btn.dataset.state;
    if (prev === state) return;

    btn.dataset.state = state;

    if (state === 'unavailable') {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.textContent = 'Unavailable';
      return;
    }

    if (state === 'loading') {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      // keep original label to avoid flicker
      btn.textContent = originalLabel;
      return;
    }

    // ready
    btn.disabled = false;
    btn.setAttribute('aria-disabled', 'false');
    btn.textContent = originalLabel;
  }

  function setupSampleButton({ selector, getSku }) {
    const btn = document.querySelector(selector);
    if (!btn || typeof getSku !== 'function') return;

    const originalLabel = getHeaderSafeText(btn) || 'Buy sample';
    btn.dataset.originalLabel = originalLabel;

    let currentVariantId = null;
    let lastSampleSku = null;

    // De-dupe fetches per SKU
    const inFlight = new Map(); // sampleSku -> Promise<{variant_id:number}>
    let requestSeq = 0;

    // Default: unavailable until resolved
    setButtonState(btn, 'unavailable', originalLabel);

    async function resolveVariantForCurrentSku({ silent = false } = {}) {
      const baseSku = normalizeBaseSku(getSku());
      if (!baseSku) {
        currentVariantId = null;
        lastSampleSku = null;
        setButtonState(btn, 'unavailable', originalLabel);
        return;
      }

      const sampleSku = toSampleSku(baseSku);

      // Already resolved for this SKU
      if (sampleSku === lastSampleSku && currentVariantId) {
        setButtonState(btn, 'ready', originalLabel);
        return;
      }

      lastSampleSku = sampleSku;

      // Cache hit
      const cached = readCachedVariantId(sampleSku);
      if (cached) {
        currentVariantId = cached;
        setButtonState(btn, 'ready', originalLabel);
        return;
      }

      // Background resolve: disable only (no label flicker)
      if (!silent) setButtonState(btn, 'loading', originalLabel);

      const seq = ++requestSeq;

      if (!inFlight.has(sampleSku)) {
        const url = `${API_ENDPOINT}?sku=${encodeURIComponent(sampleSku)}`;

        const p = (async () => {
          const res = await fetchWithTimeout(url, {
            headers: {
              'Authorization': `Bearer ${API_KEY}`,
              'Accept': 'application/json'
            }
          });

          if (!res.ok) {
            let msg = `API error (${res.status})`;
            try {
              const errBody = await res.json();
              if (errBody?.error) msg = errBody.error;
            } catch {}
            throw new Error(msg);
          }

          const data = await res.json();
          const variantId = Number(data?.variant_id);

          if (!Number.isFinite(variantId)) throw new Error('Invalid or missing variant_id');
          return { variant_id: variantId };
        })();

        inFlight.set(sampleSku, p.finally(() => inFlight.delete(sampleSku)));
      }

      try {
        const { variant_id } = await inFlight.get(sampleSku);

        // Race guard
        if (seq !== requestSeq || sampleSku !== lastSampleSku) return;

        currentVariantId = variant_id;
        writeCachedVariantId(sampleSku, variant_id);
        setButtonState(btn, 'ready', originalLabel);
      } catch (err) {
        if (seq !== requestSeq || sampleSku !== lastSampleSku) return;

        console.error('[❌] Sample resolve failed:', err?.message || err);
        currentVariantId = null;
        setButtonState(btn, 'unavailable', originalLabel);
      }
    }

    async function addToCartAndRedirect(variantId) {
      // Disable to prevent double click, but keep label stable
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (!res.ok) throw new Error('Add to cart failed');

      window.location.href = '/cart';
    }

    btn.addEventListener('click', async () => {
      // Only act when ready
      if (btn.dataset.state !== 'ready' || !currentVariantId) return;

      try {
        await addToCartAndRedirect(currentVariantId);
      } catch (err) {
        console.error('[🛑] Add to cart error:', err?.message || err);

        // Restore button state; resolve silently (no label flicker)
        btn.disabled = false;
        btn.setAttribute('aria-disabled', 'false');
        await resolveVariantForCurrentSku({ silent: true });
      }
    });

    // Initial resolve
    resolveVariantForCurrentSku();

    // SPA navigation changes
    let lastUrl = location.href;
    const observer = new MutationObserver(() => {
      const newUrl = location.href;
      if (newUrl !== lastUrl) {
        lastUrl = newUrl;
        setTimeout(() => resolveVariantForCurrentSku(), 80);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => resolveVariantForCurrentSku(), 80);
    });
  }

  setupSampleButton({
    selector: '#purchaseSampleBtn',
    getSku: () => document.querySelector('[data-ev-variant-sku]')?.textContent || null
  });

  setupSampleButton({
    selector: '#heroPurchaseSampleBtn',
    getSku: () => document.getElementById('heroPurchaseSampleBtn')?.dataset.sku || null
  });
})();